import { useQuery } from '@tanstack/react-query';
import { UserIdentitiesQueryKeys } from '../../queryKeys';
import {
  UseUserIdentitiesValidateProps,
  UseUserIdentitiesValidateQueryKeyProps,
} from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchUserIdentitiesValidate } from './service';
import { useMemo } from 'react';
import { groupBy } from '@starsoft/common/utils';
import {
  FetchUserIdentitiesValidateInputDto,
  UserIdentityValidationResultOutputDto,
} from '@starsoft/common/dto';
import { GenericError } from '@starsoft/common/models';
import { UserIdentityFormFields } from '../../../../enums/user-identity/form-fields';
import { useAxiosInstance } from '@starsoft/common/hooks';

/**
 * Custom hook to validate user identity information
 *
 * This hook handles validating user identity data based on provided parameters like identifier,
 * origin, birth date and name. It uses React Query for data fetching and validation,
 * with built-in caching and state management.
 *
 * @param {UseUserIdentitiesValidateProps} options - Optional configuration object
 * @param {string} options.identifier - The identity identifier to validate (e.g. tax ID, document number)
 * @param {string} options.originId - The origin/source of the identity
 * @param {string} options.birthDate - The birth date to validate
 * @param {string} options.name - The name to validate
 * @param {boolean} options.enabled - Optional flag to enable/disable the query (defaults to true)
 *
 * @returns {Object} An object containing:
 *   - data: The validation result data
 *   - isLoading: Boolean indicating if validation is in progress (excluding idle state)
 *   - isFetching: Boolean indicating if a fetch is in progress
 *   - isError: Boolean indicating if an error occurred
 *   - error: Any error that occurred during validation
 *   - refetch: Function to manually trigger a revalidation
 *   - errorFieldsMap: Map containing validation errors grouped by form fields
 */
export function useUserIdentitiesValidate(
  options?: UseUserIdentitiesValidateProps,
) {
  const {
    identifier,
    originId,
    enabled = true,
    birthDate,
    name,
  } = options ?? {};
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Prepare filter object for validation request
  const filter: FetchUserIdentitiesValidateInputDto = {
    identifier,
    originId,
    birthDate,
    name,
  };

  const queryKey: UseUserIdentitiesValidateQueryKeyProps = [
    UserIdentitiesQueryKeys.Validate,
    filter,
  ];

  // Setup React Query hook for validation
  const { data, isPending, isError, error, refetch, isFetching, fetchStatus } =
    useQuery<
      UserIdentityValidationResultOutputDto,
      AxiosError<GenericError>,
      UserIdentityValidationResultOutputDto,
      UseUserIdentitiesValidateQueryKeyProps
    >({
      queryKey,
      queryFn: queryContext =>
        fetchUserIdentitiesValidate(queryContext, axiosInstance),
      enabled: (!!identifier || !!birthDate || !!name) && !!originId && enabled,
      staleTime: Infinity,
      refetchOnReconnect: 'always',
      retry: 0,
    });

  // Group validation errors by form field for easier access
  const errorFieldsMap: Map<UserIdentityFormFields, string[]> = useMemo(
    () =>
      groupBy({
        records: data?.errors ?? [],
        key: 'field',
        valueKey: 'description',
      }),
    [data],
  );

  return {
    data,
    isLoading: isPending && fetchStatus !== 'idle',
    isFetching,
    isError,
    error,
    refetch,
    errorFieldsMap,
  };
}
